import type { HeaderAssemblyId } from "building-types/framing/headerAssemblyTypes";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";

/** Display value for StudioLite header assembly selects when the opening has no header. */
export const OPENING_HEADER_ASSEMBLY_NONE_DISPLAY = "None";

/** `hasHeaderAssembly: false` or resolved `headerAssemblyId: null` disables header framing. */
export function isOpeningHeaderEnabled(
  headerAssemblyId: HeaderAssemblyId | null | undefined,
  hasHeaderAssembly?: boolean,
): boolean {
  if (hasHeaderAssembly === false) {
    return false;
  }
  return headerAssemblyId !== null;
}

export function isOpeningHeaderAssemblyNoneDisplayValue(value: string): boolean {
  return value === OPENING_HEADER_ASSEMBLY_NONE_DISPLAY;
}

export function isHeaderAssemblyExplicitlyDisabled(framing: Partial<WallFramingOpeningSettings> | undefined): boolean {
  return framing !== undefined && Object.hasOwn(framing, "hasHeaderAssembly") && framing.hasHeaderAssembly === false;
}

export function hasHeaderAssemblyFramingOverride(framing: Partial<WallFramingOpeningSettings> | undefined): boolean {
  if (framing === undefined) {
    return false;
  }
  return Object.hasOwn(framing, "headerAssemblyId") || isHeaderAssemblyExplicitlyDisabled(framing);
}

export function isHeaderAssemblyIdSelectValue(value: string): value is HeaderAssemblyId {
  return !isOpeningHeaderAssemblyNoneDisplayValue(value);
}

/** Applies a header assembly catalog or widget select value to opening framing settings. */
export function applyHeaderAssemblySelectionToFraming(
  existingFraming: Partial<WallFramingOpeningSettings> | undefined,
  value: HeaderAssemblyId,
): Partial<WallFramingOpeningSettings>;
export function applyHeaderAssemblySelectionToFraming(
  existingFraming: Partial<WallFramingOpeningSettings> | undefined,
  value: null | typeof OPENING_HEADER_ASSEMBLY_NONE_DISPLAY,
): Partial<WallFramingOpeningSettings>;
export function applyHeaderAssemblySelectionToFraming(
  existingFraming: Partial<WallFramingOpeningSettings> | undefined,
  value: HeaderAssemblyId | null | typeof OPENING_HEADER_ASSEMBLY_NONE_DISPLAY,
): Partial<WallFramingOpeningSettings> {
  if (value === null || isOpeningHeaderAssemblyNoneDisplayValue(value)) {
    const { headerAssemblyId: omittedHeaderAssemblyId, ...framing } = existingFraming ?? {};
    void omittedHeaderAssemblyId;
    return { ...framing, hasHeaderAssembly: false };
  }
  const { hasHeaderAssembly: omittedHasHeaderAssembly, ...framing } = existingFraming ?? {};
  void omittedHasHeaderAssembly;
  return { ...framing, headerAssemblyId: value };
}

export function getExplicitHeaderAssemblyIdFromFraming(
  framing: Partial<WallFramingOpeningSettings> | undefined,
): HeaderAssemblyId | undefined {
  if (framing === undefined || !Object.hasOwn(framing, "headerAssemblyId")) {
    return undefined;
  }
  return framing.headerAssemblyId;
}

export function openingHeaderAssemblyFramingToWidgetDisplayValue(
  framing: Partial<WallFramingOpeningSettings> | undefined,
  resolvedHeaderAssemblyId?: HeaderAssemblyId | null,
): string | undefined {
  if (isHeaderAssemblyExplicitlyDisabled(framing)) {
    return OPENING_HEADER_ASSEMBLY_NONE_DISPLAY;
  }
  const explicitHeaderAssemblyId = getExplicitHeaderAssemblyIdFromFraming(framing);
  if (explicitHeaderAssemblyId !== undefined) {
    return explicitHeaderAssemblyId;
  }
  if (resolvedHeaderAssemblyId === null) {
    return OPENING_HEADER_ASSEMBLY_NONE_DISPLAY;
  }
  return resolvedHeaderAssemblyId;
}
